import { useCallback, useRef, useState } from 'react';

/**
 * Serialises an async action so it can never be in flight twice: calls made while it runs are
 * dropped, and `pending` drives the affordance that keeps the user from making them (a loading
 * button). The ref does the dropping because `pending` only reaches the DOM on the next render,
 * which is too late for a second click dispatched from the same task.
 *
 * The action owns its own error handling; this hook only decides when it may run.
 */
export function useSingleFlight<Args extends unknown[]>(action: (...args: Args) => Promise<void>) {
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);

  const run = useCallback(
    async (...args: Args) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setPending(true);
      try {
        await action(...args);
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [action],
  );

  return [run, pending] as const;
}
